// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { TicketCategory, TicketStatus, ticketSchema } from '@/ticket/api/types';
import { serializeDateForAPI } from '@/utilities/date-time';
import { DateTime } from 'luxon';
import { z } from 'zod';

const updateTicketResponseSchema = z
  .object({
    data: ticketSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, ticket: data }));

type UpdateTicketResponse = z.infer<typeof updateTicketResponseSchema>;

export interface UpdateTicketParameters {
  title?: string;
  description?: string | null;
  category?: TicketCategory;
  status?: TicketStatus;
  assignedTo?: string | null;
  completedBy?: string | null;
  assets?: string[];
  projects?: string[];
  completedOn?: DateTime | null;
}

/**
 * Updates an existing project in the remote API.
 *
 * @param parameters.customerId - The ID of the customer associated with the
 * project.
 * @param parameters.locationIds - The IDs of the locations associated with the
 * project.
 */
export async function updateTicket(
  key: string,
  parameters: UpdateTicketParameters
): Promise<UpdateTicketResponse> {
  const url = `/v1/ticket/${key}`;
  const response = await api.patch(url, {
    ...parameters,
    completedOn: serializeDateForAPI(parameters.completedOn),
  });

  return parseResponse(updateTicketResponseSchema, response.data, {
    schemaName: 'UpdateTicket',
    endpoint: url,
  });
}
